'use client';

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from 'react';
import { createClient } from '@/lib/supabase/client';
import { useRouter } from 'next/navigation';
import type { Session, User } from '@supabase/supabase-js';

interface UserProfile {
  id: string;
  email: string;
  full_name: string;
  role: 'OWNER' | 'PROFESSIONAL' | 'ADMIN';
}

interface BusinessProfile {
  id: string;
  verification_status: 'pending' | 'approved' | 'rejected';
}

interface AuthContextType {
  user: User | null;
  userProfile: UserProfile | null;
  businessProfile: BusinessProfile | null;
  session: Session | null;
  loading: boolean;
  signUp: (email: string, password: string, metadata: any) => Promise<any>;
  signIn: (email: string, password: string) => Promise<any>;
  signOut: () => Promise<void>;
  getCurrentUser: () => Promise<User | null>;
  isEmailVerified: () => boolean;
  getUserProfile: () => Promise<any>;
  resendVerificationEmail: () => Promise<void>;
  refreshSession: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [businessProfile, setBusinessProfile] =
    useState<BusinessProfile | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  const router = useRouter();
  const supabaseRef = useRef(createClient());
  const pendingRedirectRef = useRef(false);
  const profileLoadedForRef = useRef<string | null>(null);

  const buildFallbackProfile = useCallback(
    (userId: string, sessionUser: any): UserProfile | null => {
      const meta = sessionUser?.user_metadata;
      if (!meta) return null;
      const role = (['OWNER', 'PROFESSIONAL', 'ADMIN'].includes(meta.role)
        ? meta.role
        : 'OWNER') as UserProfile['role'];
      return {
        id: userId,
        email: sessionUser.email || meta.email || '',
        full_name: meta.full_name || sessionUser.email?.split('@')[0] || '',
        role,
      };
    },
    []
  );

  const loadUserProfile = useCallback(
    async (userId: string, sessionUser?: any): Promise<UserProfile | null> => {
      const supabase = supabaseRef.current;
      try {
        const { data, error } = await supabase
          .from('user_profiles')
          .select('*')
          .eq('id', userId)
          .single();

        if (!error && data) {
          setUserProfile(data);
          profileLoadedForRef.current = userId;

          if (data.role === 'PROFESSIONAL') {
            const { data: bizData } = await supabase
              .from('business_profiles')
              .select('id, verification_status')
              .eq('user_id', userId)
              .maybeSingle();
            setBusinessProfile(bizData || null);
          }

          return data;
        }

        if (sessionUser) {
          const fallback = buildFallbackProfile(userId, sessionUser);
          if (fallback) {
            setUserProfile(fallback);
            profileLoadedForRef.current = userId;
            return fallback;
          }
        }

        setUserProfile(null);
        profileLoadedForRef.current = null;
        return null;
      } catch (err) {
        console.error('[AuthContext] Error loading user profile:', err);
        if (sessionUser) {
          const fallback = buildFallbackProfile(userId, sessionUser);
          if (fallback) {
            setUserProfile(fallback);
            profileLoadedForRef.current = userId;
            return fallback;
          }
        }
        setUserProfile(null);
        profileLoadedForRef.current = null;
        return null;
      }
    },
    [buildFallbackProfile]
  );

  useEffect(() => {
    const supabase = supabaseRef.current;
    let mounted = true;

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, newSession) => {
      if (!mounted) return;

      setSession(newSession);
      setUser(newSession?.user ?? null);

      if (event === 'SIGNED_OUT') {
        setUserProfile(null);
        setBusinessProfile(null);
        profileLoadedForRef.current = null;
        setLoading(false);
        return;
      }

      if (
        (event === 'INITIAL_SESSION' || event === 'SIGNED_IN') &&
        newSession?.user
      ) {
        const alreadyLoaded =
          profileLoadedForRef.current === newSession.user.id;
        if (event === 'INITIAL_SESSION' && alreadyLoaded) {
          setLoading(false);
          return;
        }

        const profile = await loadUserProfile(
          newSession.user.id,
          newSession.user
        );
        if (!mounted) return;
        setLoading(false);

        if (event === 'SIGNED_IN' && pendingRedirectRef.current && profile) {
          pendingRedirectRef.current = false;
          const dest = profile.role === 'ADMIN' ? '/admin' : '/dashboard';
          router.push(dest);
        }
        return;
      }

      if (event === 'TOKEN_REFRESHED' && newSession?.user) {
        if (profileLoadedForRef.current !== newSession.user.id) {
          await loadUserProfile(newSession.user.id, newSession.user);
        }
      }

      if (!newSession) {
        setLoading(false);
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [loadUserProfile, router]);

  const signUp = async (email: string, password: string, metadata: any) => {
    const supabase = supabaseRef.current;
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: metadata?.fullName || '',
          role: metadata?.role || 'OWNER',
        },
        emailRedirectTo: `${window.location.origin}/auth/callback?next=${metadata?.role === 'PROFESSIONAL' ? '/business' : '/dashboard'}`,
      },
    });
    if (error) throw error;
    return data;
  };

  const signIn = async (email: string, password: string) => {
    const supabase = supabaseRef.current;
    pendingRedirectRef.current = true;
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) {
      pendingRedirectRef.current = false;
      throw error;
    }
    return data;
  };

  const signOut = async () => {
    const supabase = supabaseRef.current;
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    setUserProfile(null);
    setBusinessProfile(null);
    profileLoadedForRef.current = null;
    router.push('/login');
  };

  const getCurrentUser = async () => {
    const supabase = supabaseRef.current;
    const {
      data: { user },
      error,
    } = await supabase.auth.getUser();
    if (error) throw error;
    return user;
  };

  const isEmailVerified = () => {
    return user?.email_confirmed_at != null;
  };

  const getUserProfile = async () => {
    const supabase = supabaseRef.current;
    if (!user) return null;
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', user.id)
      .single();
    if (error) throw error;
    return data;
  };

  const resendVerificationEmail = async () => {
    const supabase = supabaseRef.current;
    if (!user?.email) throw new Error('No email address found');
    const { error } = await supabase.auth.resend({
      type: 'signup',
      email: user.email,
      options: {
        emailRedirectTo: `${window.location.origin}/auth/callback`,
      },
    });
    if (error) throw error;
  };

  const refreshSession = async () => {
    const supabase = supabaseRef.current;
    const { data, error } = await supabase.auth.refreshSession();
    if (error) throw error;
    if (data.session) {
      setSession(data.session);
      setUser(data.session.user);
    }
  };

  const value = {
    user,
    userProfile,
    businessProfile,
    session,
    loading,
    signUp,
    signIn,
    signOut,
    getCurrentUser,
    isEmailVerified,
    getUserProfile,
    resendVerificationEmail,
    refreshSession,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
